
import { useUserContext } from "../context/DealerProfile.jsx";
import { useEffect, useState } from "react";
import useSWR from "swr";

export function useTasks() {
    const { dealerDetail } = useUserContext();

    const [birthdays, setBirthdays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setError] = useState(null);

    // Tasks fetcher
    const fetchTasksFetcher = async (url) => {
        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch tasks");

        const data = await res.json();

        const now = new Date();

        return (data.data || []).filter(task => {
            const dueRaw = task.Due_Date_Time || task.Due_Date;
            const dueDate = new Date(dueRaw);
            const status = (task.Status || "").toLowerCase();
            const catalystEmail = task.Catalyst_User_Email?.trim();

            return (
                catalystEmail &&
                dueRaw &&
                !isNaN(dueDate) &&
                dueDate > now &&
                status !== "completed"
            );
        });
    };

    const url = dealerDetail?.Email
        ? `/server/dealer_backend/getDueTasks?dealerEmail=${encodeURIComponent(dealerDetail.Email)}`
        : null;

    const {
        data: tasks,
        error,
        isLoading
    } = useSWR(url, fetchTasksFetcher, {
        refreshInterval: 60000,
        revalidateOnFocus: false,
        revalidateOnReconnect: true,
    });

    // Birthday fetching
    useEffect(() => {
        const donorTypeRaw = dealerDetail?.Donor_Type;
        const email = dealerDetail?.Email;

        // Extract actual array from List_Name object
        const listName = Array.isArray(dealerDetail?.List_Name)
    ? dealerDetail.List_Name
    : dealerDetail?.List_Name?.listName || [];

const pListName = Array.isArray(dealerDetail?.Potential_Donor_List_Name)
    ? dealerDetail.Potential_Donor_List_Name
    : dealerDetail?.Potential_Donor_List_Name?.listName || [];

        console.log("Birthday List Names:", listName);

        if (!email) {
            setBirthdays([]);
            setLoading(false);
            return;
        }

        const donorType = donorTypeRaw?.trim().toLowerCase();

        // Unique cache key based on email, donor type and donor lists
        const sortedListNames = [...listName].sort();

        const cacheKey = `birthdays_${email}_${donorType}_${JSON.stringify({
    existing: [...listName].sort(),
    potential: [...pListName].sort()
})}`;

        const fetchBirthdays = async () => {
            setLoading(true);
            setError(null);

            try {
                let responses = [];

                if (donorType === "potential donor") {

                    responses.push(
                        fetch(`/server/dealer_backend/getLeadsBirthdays`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                dealerEmail: email,
                                listName: pListName
                            }),
                        }).then(async res => {
                            if (!res.ok) throw new Error("Failed to fetch lead birthdays");

                            const data = await res.json();

                            return (data.data || []).map(item => ({
                                ...item,
                                source: "lead"
                            }));
                        })
                    );

                } else if (donorType === "existing donor") {

                    responses.push(
                        fetch(`/server/dealer_backend/getBirthdays`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                dealerEmail: email,
                                listName: listName
                            }),
                        }).then(async res => {
                            if (!res.ok) throw new Error("Failed to fetch contact birthdays");

                            const data = await res.json();

                            return (data.data || []).map(item => ({
                                ...item,
                                source: "contact"
                            }));
                        })
                    );

                } else {

                    responses.push(
                        fetch(`/server/dealer_backend/getBirthdays`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                dealerEmail: email,
                                listName : listName
                            }),
                        }).then(async res => {
                            if (!res.ok) throw new Error("Failed to fetch contact birthdays");

                            const data = await res.json();

                            return (data.data || []).map(item => ({
                                ...item,
                                source: "contact"
                            }));
                        })
                    );

                    responses.push(
                        fetch(`/server/dealer_backend/getLeadsBirthdays`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                dealerEmail: email,
                                listName: pListName
                            }),
                        }).then(async res => {
                            if (!res.ok) throw new Error("Failed to fetch lead birthdays");

                            const data = await res.json();

                            return (data.data || []).map(item => ({
                                ...item,
                                source: "lead"
                            }));
                        })
                    );
                }

                const allPeople = (await Promise.all(responses)).flat();

                // Save data using list-specific cache key
                const today = new Date().toISOString().split("T")[0];

                localStorage.setItem(cacheKey, JSON.stringify(allPeople));
                localStorage.setItem(`${cacheKey}_date`, today);

                setBirthdays(allPeople);

            } catch (err) {
                console.error("Birthday fetch error:", err);
                setError(err.message || "Unknown error");
            } finally {
                setLoading(false);
            }
        };

        // Check list-specific cache
        const today = new Date().toISOString().split("T")[0];

        const cachedDate = localStorage.getItem(`${cacheKey}_date`);
        const cachedData = localStorage.getItem(cacheKey);

        if (cachedDate === today && cachedData) {
            console.log("Using cached birthdays:", cacheKey);

            setBirthdays(JSON.parse(cachedData));
            setLoading(false);

        } else {
            console.log("Fetching fresh birthdays:", cacheKey);

            fetchBirthdays();
        }

    }, [
        dealerDetail?.Email,
        dealerDetail?.Donor_Type,
        dealerDetail?.List_Name
    ]);

    return {
        tasks,
        birthdays,
        loading: loading || isLoading,
        error: errors || error
    };
}
